import { ExpandLess } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material'
import { ContainerDto, UnitType } from '@planning/app/api'
import { SearchAutocomplete } from '@planning/components/list/SearchAutocomplete'
import { SimpleList } from '@planning/components/list/SimpleList'
import { IListStore } from '@planning/components/list/SimpleListStore'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { ContainerAutocompleteStore } from '../stores/ContainerAutocompleteStore'
import { ContainerVisit } from '../stores/ContainerVisitItem'
import { CreateOrderPageViewStore } from '../stores/CreateOrderPageViewStore'
import { SelectOrderViewStore } from '../stores/SelectOrderViewStore'
import { OrderFormStore } from '../V2/stores/OrderFormStore'
import { ContainerAutocompleteListItem } from './ContainerAutocompleteListItem'
import { ContainerAutocompleteListItemLoader } from './ContainerAutocompleteListItemLoader'
import { ContainerVisitPaperLoader } from './ContainerVisitPaperLoader'
import { OpenOrdersAlert } from './OpenOrdersAlert'
import { OrdersListCard } from './OrdersListCard'
import { UnitContainerCard } from './StepperForm/molecules/UnitContainerCard'
import { AddUnitInfoFormWrapper } from './StepperForm/organisms/AddUnitInfoFormWrapper'

interface IProps {
  listStore: IListStore<ContainerVisit>
  viewStore: SelectOrderViewStore
  formStore: OrderFormStore
  createOrderPageViewStore: CreateOrderPageViewStore
  onEditOrder?: (containerVisit: ContainerVisit) => void
  onDeleteOrder?: (containerVisit: ContainerVisit) => Promise<void>
  orderCreationOnly?: boolean
  prefilledUnitNumber?: string
}

export const SearchContainerVisitDetailed = observer(
  ({
    listStore,
    viewStore,
    formStore,
    createOrderPageViewStore,
    onEditOrder,
    onDeleteOrder,
    orderCreationOnly,
    prefilledUnitNumber,
  }: IProps) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const [showContainerForm, setShowContainerForm] = useState(false)

    const containerAutocompleteStore = useMemo(() => {
      const store = new ContainerAutocompleteStore()

      store.setIncludeRestow(true)
      store.setSelectedUnitTypeFilter(UnitType.Container)

      if (createOrderPageViewStore.container) {
        store.setValue({
          ...createOrderPageViewStore.container,
          activeOrderIds: [],
        })
      }
      return store
    }, [createOrderPageViewStore.container])

    useEffect(() => {
      if (prefilledUnitNumber) {
        containerAutocompleteStore.setFilter(prefilledUnitNumber)
      }
    }, [prefilledUnitNumber, containerAutocompleteStore])

    const cleanData = () => {
      setShowContainerForm(false)
      containerAutocompleteStore.setValue(undefined)
      viewStore.selectContainer(undefined)
      createOrderPageViewStore.reset()
      if (listStore && listStore.reset) listStore.reset()
    }

    const renderAutocompleteOption = (option: ContainerDto) => {
      return <ContainerAutocompleteListItem container={option} />
    }

    const CreateOrderFromNewContainer = () => (
      <Box sx={{ mt: '1rem' }}>
        <Button onClick={() => setShowContainerForm(true)} data-cy='create-new-container-btn'>
          {t('createNewUnit', 'Create new unit')}
        </Button>

        {showContainerForm && (
          <AddUnitInfoFormWrapper
            formStore={formStore}
            containerNumber={containerAutocompleteStore.filter}
            unitType={containerAutocompleteStore.selectedUnitTypeFilter ?? UnitType.Container}
            createOrderPageViewStore={createOrderPageViewStore}
          />
        )}
      </Box>
    )

    const unitTypeLabelMap: Record<string, string> = {
      Container: t('containerSelected', 'Container selected'),
      SwapBody: t('swapBodySelected', 'Swap Body selected'),
      Trailer: t('trailerSelected', 'Trailer selected'),
    }

    const hasOpenOrders = listStore.items && listStore.items.length > 0

    return (
      <>
        <Box
          sx={{
            p: '1rem',
            border: `1px solid ${theme.palette.grey[300]}`,
            background: '#fff',
            borderRadius: '8px',
            mb: '1rem',
          }}
        >
          <Stack direction='row' spacing={1} mb='1.5rem'>
            <Button
              variant='contained'
              size='small'
              color={
                containerAutocompleteStore.selectedUnitTypeFilter === UnitType.Container
                  ? 'secondary'
                  : 'inherit'
              }
              onClick={() => {
                cleanData()
                containerAutocompleteStore.setSelectedUnitTypeFilter(UnitType.Container)
              }}
            >
              {t('container', 'Container')}
            </Button>
            <Button
              variant='contained'
              size='small'
              color={
                containerAutocompleteStore.selectedUnitTypeFilter === UnitType.SwapBody
                  ? 'secondary'
                  : 'inherit'
              }
              onClick={() => {
                cleanData()
                containerAutocompleteStore.setSelectedUnitTypeFilter(UnitType.SwapBody)
              }}
            >
              {t('swapBody', 'SwapBody')}
            </Button>
            <Button
              variant='contained'
              size='small'
              color={
                containerAutocompleteStore.selectedUnitTypeFilter === UnitType.Trailer
                  ? 'secondary'
                  : 'inherit'
              }
              onClick={() => {
                cleanData()
                containerAutocompleteStore.setSelectedUnitTypeFilter(UnitType.Trailer)
              }}
            >
              {t('trailer', 'Trailer')}
            </Button>
          </Stack>

          <SearchAutocomplete
            label={t('searchUnitNumber', 'Search unit number')}
            store={containerAutocompleteStore}
            renderOption={renderAutocompleteOption}
            renderLoadingOption={() => <ContainerAutocompleteListItemLoader />}
            getOptionLabel={c => c.number ?? ''}
            onFilterChange={cleanData}
            onChange={async (filter, value) => {
              listStore.setFilter(filter)
              if (value && typeof value !== 'string') {
                viewStore.selectContainer(value)
                createOrderPageViewStore.selectContainer(value)
              }
            }}
            testId={'container-visit-containers'}
          />

          {containerAutocompleteStore.value && (
            <Box>
              <Typography variant='subtitle1' mt='2rem' mb='1rem'>
                {unitTypeLabelMap[
                  containerAutocompleteStore.selectedUnitTypeFilter ?? UnitType.Container
                ] ?? t('unitSelected', 'Unit selected')}
              </Typography>
              <UnitContainerCard container={containerAutocompleteStore.value} />
            </Box>
          )}

          {containerAutocompleteStore.filter &&
            containerAutocompleteStore.filteredItems.length === 0 && (
              <CreateOrderFromNewContainer />
            )}

          {hasOpenOrders && <OpenOrdersAlert />}
        </Box>

        {hasOpenOrders && (
          <Box
            sx={{
              p: '1rem',
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <Accordion
              disableGutters
              elevation={0}
              square
              sx={{
                '& .MuiAccordionSummary-content': {
                  flexGrow: 0,
                  marginRight: '4px',
                },
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  boxShadow: 'none',
                },
              }}
            >
              <AccordionSummary
                sx={{ m: 0, p: 0, justifyContent: 'flex-start' }}
                expandIcon={<ExpandLess />}
              >
                <Typography variant='body1'>
                  {t('openOrder', 'Open order')} ({listStore.items.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ m: 0, p: 0 }}>
                <SimpleList
                  store={listStore}
                  renderListItem={(containerVisit: ContainerVisit, i: number) => {
                    const handleEdit = () => onEditOrder?.(containerVisit)
                    return (
                      <OrdersListCard
                        orderCreationOnly={orderCreationOnly}
                        containerVisit={containerVisit}
                        container={viewStore.selectedContainer}
                        key={`container-visit-paper-${i}`}
                        // [REVIEW] TODO: onEditOrder and onDeleteOrder are undefined
                        onClickEdit={!onEditOrder ? undefined : handleEdit}
                        onClickDelete={async () => await onDeleteOrder?.(containerVisit)}
                      />
                    )
                  }}
                  renderLoadingListItem={() => <ContainerVisitPaperLoader />}
                  showTotalResults={false}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </>
    )
  },
)
